/**
 * HeroSection - 랜딩페이지 히어로 섹션
 *
 * 핵심 메시지와 CTA 버튼을 포함한 첫 화면
 * - 헤드라인: 학생에게 답을 주지 않고도 즉시 채점
 * - 서브카피: 채점 시간 절약 + 답 노출 방지
 * - CTA: 베타 서비스 무료로 시작하기
 */

import React from 'react';
import { Typography, Button, Space } from 'antd';
import { RocketOutlined, LoginOutlined, DashboardOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

const { Title, Text } = Typography;

export const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  /**
   * 로그인된 사용자의 역할에 따른 대시보드 경로 반환
   */
  const getDashboardPath = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case 'admin':
        return '/system';
      case 'group_admin':
      case 'instructor':
      default:
        return '/dashboard';
    }
  };

  return (
    <section
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '80px 24px',
        textAlign: 'center',
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 서비스 로고/이름 */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '16px',
          padding: '8px 24px',
          marginBottom: '24px',
        }}
      >
        <Text
          style={{
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            letterSpacing: '2px',
          }}
        >
          BETA SERVICE
        </Text>
      </div>

      {/* 메인 헤드라인 */}
      <Title
        level={1}
        style={{
          color: '#fff',
          fontSize: 'clamp(28px, 5vw, 48px)',
          fontWeight: 700,
          marginBottom: '16px',
          lineHeight: 1.3,
          maxWidth: '800px',
        }}
      >
        학생에게 답을 주지 않고도
        <br />
        즉시 채점
      </Title>

      {/* 서브카피 */}
      <Text
        style={{
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: 'clamp(16px, 2.5vw, 20px)',
          display: 'block',
          marginBottom: '40px',
          maxWidth: '600px',
          lineHeight: 1.6,
        }}
      >
        채점 시간은 줄이고, 답 노출은 막고
        <br />
        학원 수학 채점의 새로운 방법
      </Text>

      {/* CTA 버튼 - 로그인 상태에 따라 다르게 표시 */}
      <Space size="middle" wrap style={{ justifyContent: 'center' }}>
        {isAuthenticated ? (
          // 로그인된 사용자: 대시보드 이동 버튼
          <Button
            type="primary"
            size="large"
            icon={<DashboardOutlined />}
            onClick={() => navigate(getDashboardPath())}
            style={{
              height: '56px',
              padding: '0 40px',
              fontSize: '18px',
              fontWeight: 600,
              borderRadius: '28px',
              background: '#fff',
              color: '#667eea',
              border: 'none',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
            }}
          >
            대시보드로 이동
          </Button>
        ) : (
          // 비로그인 사용자: 회원가입 + 로그인 버튼
          <>
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={() => navigate('/signup')}
              style={{
                height: '56px',
                padding: '0 40px',
                fontSize: '18px',
                fontWeight: 600,
                borderRadius: '28px',
                background: '#fff',
                color: '#667eea',
                border: 'none',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
              }}
            >
              베타 서비스 무료로 시작하기
            </Button>
            <Button
              size="large"
              icon={<LoginOutlined />}
              onClick={() => navigate('/login')}
              style={{
                height: '56px',
                padding: '0 32px',
                fontSize: '16px',
                borderRadius: '28px',
                background: 'transparent',
                color: '#fff',
                border: '2px solid rgba(255, 255, 255, 0.5)',
              }}
            >
              로그인
            </Button>
          </>
        )}
      </Space>

      {/* 부가 정보 - 로그인 상태에 따라 다르게 표시 */}
      <Text
        style={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '14px',
          marginTop: '24px',
        }}
      >
        {isAuthenticated
          ? `${user?.name || '사용자'}님, 환영합니다!`
          : '가입 즉시 무료 이용 가능'}
      </Text>
    </section>
  );
};
